import { STORAGE } from "./config";
import { easternWeekId } from "./game/score";
import { isServedZip, lookupZip, normalizeZip, OUT_OF_AREA_MESSAGE } from "./zips";

export interface BoardRow {
  name: string;
  score: number;
  housesLit: number;
}

export interface BoardResponse {
  weekId: string;
  scores: BoardRow[];
  stub: boolean;
  source: "d1" | "kv" | "local";
}

export interface LeadPayload {
  name: string;
  phone: string;
  zip: string;
  email?: string;
  emailScore: boolean;
  score: number;
  housesLit: number;
}

export interface LeadResult {
  ok: boolean;
  served: boolean;
  message: string;
  city?: string;
  county?: string;
}

export async function fetchBoard(): Promise<BoardResponse> {
  try {
    const res = await fetch("/api/leaderboard", { headers: { Accept: "application/json" } });
    if (res.ok) {
      const data = (await res.json()) as BoardResponse;
      if (Array.isArray(data.scores)) return data;
    }
  } catch {
    /* local fallback */
  }
  return localBoard();
}

export async function submitScore(name: string, score: number, housesLit: number): Promise<BoardResponse> {
  const clean = name.trim().slice(0, 16) || "Neighbor";
  try {
    const res = await fetch("/api/leaderboard", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: clean, score, housesLit }),
    });
    if (res.ok) return (await res.json()) as BoardResponse;
  } catch {
    /* local fallback */
  }
  return pushLocalScore(clean, score, housesLit);
}

export async function submitLead(payload: LeadPayload): Promise<LeadResult> {
  const zip = normalizeZip(payload.zip);
  const info = lookupZip(zip);
  if (!info) {
    return { ok: false, served: false, message: OUT_OF_AREA_MESSAGE };
  }
  const body = {
    name: payload.name.trim().slice(0, 80),
    phone: payload.phone.replace(/\D/g, "").slice(0, 15),
    zip,
    email: payload.email?.trim() ?? "",
    emailScore: payload.emailScore,
    score: payload.score,
    housesLit: payload.housesLit,
    source: "sasquatch-roof-runner",
    company_website: "",
  };

  try {
    const res = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as LeadResult;
    if (typeof data.ok === "boolean") return data;
  } catch {
    /* local fallback */
  }

  const leads = readJson<LeadPayload[]>(STORAGE.localLeads, []);
  leads.push({ ...payload, zip });
  localStorage.setItem(STORAGE.localLeads, JSON.stringify(leads.slice(-40)));
  return {
    ok: true,
    served: true,
    city: info.city,
    county: info.county,
    message:
      "Quote request saved on this device. Add WEBHOOK_URL or a Web3Forms key on Cloudflare to send it to the shop.",
  };
}

export function validateLeadClient(input: {
  name: string;
  phone: string;
  zip: string;
  emailScore: boolean;
  email: string;
}): { ok: boolean; message: string } {
  if (input.name.trim().length < 2) return { ok: false, message: "Tell us your name so we know who to call." };
  const digits = input.phone.replace(/\D/g, "");
  if (digits.length < 10) return { ok: false, message: "Need a 10-digit phone number." };
  if (!isServedZip(input.zip)) return { ok: false, message: OUT_OF_AREA_MESSAGE };
  if (input.emailScore && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email.trim())) {
    return { ok: false, message: "Add an email if you want your score sent over." };
  }
  return { ok: true, message: "" };
}

function localBoard(): BoardResponse {
  const weekId = easternWeekId();
  const stored = readJson<{ weekId: string; scores: BoardRow[] }>(STORAGE.localWeek, {
    weekId,
    scores: [],
  });
  const scores = stored.weekId === weekId ? stored.scores : [];
  return { weekId, scores, stub: true, source: "local" };
}

function pushLocalScore(name: string, score: number, housesLit: number): BoardResponse {
  const board = localBoard();
  board.scores.push({ name, score, housesLit });
  board.scores.sort((a, b) => b.score - a.score || b.housesLit - a.housesLit);
  board.scores = board.scores.slice(0, 10);
  localStorage.setItem(STORAGE.localWeek, JSON.stringify({ weekId: board.weekId, scores: board.scores }));
  return board;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
